import {
  inMemoryPersistence,
  setPersistence,
  signInWithCustomToken,
  signOut,
} from "firebase/auth";
import { getRestaurantFirebaseAuth } from "./config";

export type FirebaseSessionAdapter = {
  signIn: (customToken: string) => Promise<void>;
  signOut: () => Promise<void>;
};

/**
 * Firebase credentials are intentionally memory-only on shared restaurant
 * devices. The HttpOnly seller session remains the source of truth, and
 * unmount/logout signs Firebase out so a later staff member cannot inherit the
 * previous user's Firestore identity.
 */
const sdkSessionAdapter: FirebaseSessionAdapter = {
  async signIn(customToken) {
    const auth = getRestaurantFirebaseAuth();
    if (!auth) throw new Error("Firebase Auth is unavailable");
    const generation = sdkQueue.generation;
    await setPersistence(auth, inMemoryPersistence);
    if (generation !== sdkQueue.generation) return;
    await signInWithCustomToken(auth, customToken);
  },
  async signOut() {
    const auth = getRestaurantFirebaseAuth();
    if (!auth) return;
    await signOut(auth);
  },
};

type AuthQueue = { adapter: FirebaseSessionAdapter; generation: number; tail: Promise<void> };
const queues = new WeakMap<FirebaseSessionAdapter, AuthQueue>();
function queueFor(adapter: FirebaseSessionAdapter): AuthQueue {
  let queue = queues.get(adapter);
  if (!queue) { queue = { adapter, generation: 0, tail: Promise.resolve() }; queues.set(adapter, queue); }
  return queue;
}
function enqueue(queue: AuthQueue, operation: () => Promise<void>) {
  const next = queue.tail.catch(() => undefined).then(operation); queue.tail = next; return next;
}
const sdkQueue = queueFor(sdkSessionAdapter);
/** The transition helper and every provider mount share the same SDK queue. */
export const firebaseSessionAdapter: FirebaseSessionAdapter = {
  signIn(token) { const generation = sdkQueue.generation; return enqueue(sdkQueue, async () => { if (generation === sdkQueue.generation) await sdkSessionAdapter.signIn(token); }); },
  signOut() { sdkQueue.generation++; return enqueue(sdkQueue, () => sdkSessionAdapter.signOut()); },
};
queues.set(firebaseSessionAdapter, sdkQueue);
/** A provider's cleanup can release only its own authentication lease. */
export function ownRestaurantFirebaseSession(adapter: FirebaseSessionAdapter) {
  const queue = queueFor(adapter); const generation = ++queue.generation;
  const isCurrent = () => queue.generation === generation;
  return {
    isCurrent,
    signIn: (token: string) => enqueue(queue, async () => { if (isCurrent()) await queue.adapter.signIn(token); }),
    signOut: () => enqueue(queue, async () => { if (isCurrent()) await queue.adapter.signOut(); }),
    release: () => {
      if (!isCurrent()) return Promise.resolve();
      const released = ++queue.generation;
      return enqueue(queue, async () => { if (queue.generation === released) await queue.adapter.signOut(); });
    },
  };
}
